import { request } from '@umijs/max';
import OpenAI from 'openai';
import { getNebulithInferenceProApiUrl } from '@/services/util';

/** 浏览器侧 OpenAI v1 baseURL：当前页 origin + nebulith-inference-pro 前缀 */
export function getModelExperienceOpenAIV1BaseURL() {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  return `${origin}${getNebulithInferenceProApiUrl('preview/v1')}`;
}

function createModelExperienceOpenAIFetch(signal?: AbortSignal) {
  return (input: RequestInfo | URL, init?: RequestInit) => {
    const headers = new Headers(init?.headers as HeadersInit);
    const accessToken =
      typeof window !== 'undefined'
        ? localStorage.getItem('accessToken')
        : null;
    if (accessToken) {
      headers.set('Authorization', `Bearer ${accessToken}`);
    }
    return fetch(input, {
      ...init,
      headers,
      credentials: 'include',
      signal: signal ?? init?.signal,
    });
  };
}

/** LLM 流式对话等：浏览器直连 OpenAI v1 */
export function createExploreOpenAIClient(signal?: AbortSignal) {
  return new OpenAI({
    baseURL: getModelExperienceOpenAIV1BaseURL(),
    apiKey: 'unused',
    dangerouslyAllowBrowser: true,
    fetch: createModelExperienceOpenAIFetch(signal),
  });
}

/**
 * 浏览器直连 OpenAI v1（语音/文生图等）。
 * @param outerSignal 传入时合并到 fetch，便于中止 images.generate 等请求。
 */
export function createExploreSpeechOpenAIClient(outerSignal?: AbortSignal) {
  return new OpenAI({
    baseURL: getModelExperienceOpenAIV1BaseURL(),
    apiKey: 'unused',
    dangerouslyAllowBrowser: true,
    fetch: createModelExperienceOpenAIFetch(outerSignal),
  });
}

export interface EmbeddingsRequest {
  input: string[];
  model: string;
}

export interface EmbeddingsResponseItem {
  index?: number;
  object?: string;
  embedding: number[];
}

export interface EmbeddingsUsage {
  prompt_tokens?: number;
  total_tokens?: number;
  completion_tokens?: number;
  prompt_tokens_details?: unknown;
}

export interface EmbeddingsResponseBody {
  id?: string;
  object?: string;
  created?: number;
  model?: string;
  data?: EmbeddingsResponseItem[];
  usage?: EmbeddingsUsage;
}

export async function embeddings(data: EmbeddingsRequest) {
  return request<EmbeddingsResponseBody>(
    getNebulithInferenceProApiUrl('preview/v1/embeddings'),
    {
      method: 'POST',
      data,
    },
  );
}

export interface PcaDocumentInput {
  content: string;
  embedding: number[];
  title: string;
}

export interface PcaRequestBody {
  documents: PcaDocumentInput[];
}

export interface D2Position {
  x: number;
  y: number;
}

export interface DocumentPcaPosition {
  content: string;
  d2Position: D2Position | null;
  title: string;
}

/** 向量 PCA 降维 */
// todo
export async function getPcaResult(params: PcaRequestBody) {
  return request<DocumentPcaPosition[]>(
    getNebulithInferenceProApiUrl('api/pca'),
    {
      method: 'POST',
      data: params,
    },
  );
}

export interface RerankRequest {
  documents: string[];
  model: string;
  query: string;
}

export interface RerankResultDocument {
  text?: string;
  multi_modal?: unknown;
}

export interface RerankResultItem {
  index?: number;
  document?: RerankResultDocument | null;
  relevance_score?: number;
}

export interface RerankUsage {
  total_tokens?: number;
}

export interface RerankResponse {
  id?: string;
  model?: string;
  usage?: RerankUsage;
  results?: RerankResultItem[];
}

export async function rerank(data: RerankRequest) {
  return request<RerankResponse>(
    getNebulithInferenceProApiUrl('preview/v1/rerank'),
    {
      method: 'POST',
      data,
    },
  );
}
